import type { IFilter } from '@models/Filter'
import { endOfDay, startOfDay } from '@util/date-util'

import { apiClient } from '../../../gateways/api'
import type { ActiviteitenSearch } from '../types/ActiviteitenSearch'

export { initialState, createFilters, searchActiviteiten }

const initialState: ActiviteitenSearch = {
  data: { count: 0, skip: 0, limit: 25, items: [] },
  query: {
    filters: {
      gebied: [],
      type: [],
      assignedTo: [],
      fromChangedDate: null,
      toChangedDate: null,
      fromDate: null,
      toDate: null,
      labels: [],
      myList: false,
      startUitvoeringWeekTot: '',
      startUitvoeringWeekVan: '',
      gemeente: [],
      verantwoordelijke: null,
      contactpersoon: null,
      typeWerk: [],
      due: true,
      processtatus: [],
      kpi: '',
    },
    sort: { sortBy: 'created.date', sortDirection: 'desc' },
    page: { skip: 0, limit: 25 },
  },
}

const createFilters = ({
  gebied,
  type,
  assignedTo,
  toDate,
  fromDate,
  toChangedDate,
  fromChangedDate,
  labels,
  myList,
  startUitvoeringWeekVan,
  startUitvoeringWeekTot,
  gemeente,
  verantwoordelijke,
  contactpersoon,
  typeWerk,
  due,
  processtatus,
  kpi,
}) => {
  const filters: IFilter[] = [
    { name: 'status', value: 'open', operator: 'eq' },
    { name: 'isActive', value: true, operator: 'eq' },
  ]

  if (type && type.length > 0)
    filters.push({
      name: 'type',
      value: type.map(({ code }) => code),
      operator: 'in',
    })
  if (gebied && gebied.length > 0)
    filters.push({
      name: 'entity.gebiedscode',
      value: gebied.map(({ code }) => code),
      operator: 'in',
    })
  if (assignedTo && assignedTo.length > 0)
    filters.push({
      name: 'assignedTo',
      value: assignedTo.map(({ label }) => label),
      operator: 'in',
    })
  if (fromDate)
    filters.push({
      name: 'dueDate',
      value: startOfDay(fromDate),
      operator: 'gte',
    })
  if (toDate)
    filters.push({ name: 'dueDate', value: endOfDay(toDate), operator: 'lte' })
  if (fromChangedDate)
    filters.push({
      name: 'changed',
      value: startOfDay(fromChangedDate),
      operator: 'gte',
    })
  if (toChangedDate)
    filters.push({
      name: 'changed',
      value: endOfDay(toChangedDate),
      operator: 'lte',
    })

  if (labels && labels.length > 0)
    filters.push({
      name: 'labels',
      value: labels.map(({ code }) => code),
      operator: 'in',
    })

  if (startUitvoeringWeekVan)
    filters.push({
      name: 'searchValues.startUitvoeringWeek',
      value: startUitvoeringWeekVan,
      operator: 'gte',
    })
  if (startUitvoeringWeekTot)
    filters.push({
      name: 'searchValues.startUitvoeringWeek',
      value: startUitvoeringWeekTot,
      operator: 'lte',
    })
  if (gemeente.length > 0)
    filters.push({
      name: 'searchValues.gemeente',
      value: gemeente,
      operator: 'in',
    })

  if (verantwoordelijke)
    filters.push({
      name: 'searchValues.verantwoordelijken',
      value: verantwoordelijke.code,
      operator: 'eq',
    })

  if (contactpersoon)
    filters.push({
      name: 'contactpersoon',
      operator: 'eq',
      value: contactpersoon.userid,
    })

  filters.push({
    name: 'entity.projecttype',
    value: ['Hoofdleiding'],
    operator: 'in',
  })
  if (myList) {
    filters.push({ name: 'my-list', value: true, operator: 'eq' })
  }
  if (Array.isArray(typeWerk) && typeWerk.length > 0) {
    filters.push({
      name: 'searchValues.typeWerk',
      operator: 'in',
      value: typeWerk,
    })
  }

  if (due) {
    filters.push({
      name: 'due',
      value: true,
      operator: 'eq',
    })
  }

  if (processtatus && processtatus.length > 0) {
    filters.push({
      name: 'searchValues.statusText',
      value: processtatus,
      operator: 'in',
    })
  }

  if (kpi) {
    filters.push({ name: 'kpi', value: kpi, operator: 'eq' })
  }

  return filters
}

const searchActiviteiten = async ({
  query,
}: {
  query: ActiviteitenSearch['query']
}): Promise<ActiviteitenSearch['data']> => {
  const { filters, page, sort } = query

  const { data } = await apiClient({
    url: '/rest/activiteit/search',
    method: 'POST',
    data: { filters: createFilters(filters), ...page, ...sort },
  })

  return data
}
